import React, { MouseEvent, useEffect, useState } from 'react';
import { Button, Grid, InputAdornment, styled, TextField } from '@mui/material';
import Dialog from 'components/dialogs/Dialog';
import { HospitalProcedure } from 'types/hospital';
import { Procedure } from 'types/procedure';
import { toast } from 'react-toastify';
import ProceduresAutoComplete from 'components/autocomplete/ProceduresAutoComplete';
import MoneyInput from 'components/masked-input/MoneyInput';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface HospitalProcedureFormProps {
  onExited: () => void;
  handleAddProcedure: (procedureInfo: HospitalProcedure | null) => void;
  procedureInfo: HospitalProcedure | null;
  handleChangeProcedureInfo(index: keyof HospitalProcedure, value: any): void;
  type: 'new' | 'edit';
}

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  columnGap: 10,
  rowGap: 15,
});

const DialogActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
});

const HospitalProcedureForm: React.FC<HospitalProcedureFormProps> = ({
  handleAddProcedure,
  onExited,
  procedureInfo,
  handleChangeProcedureInfo,
  type,
}) => {
  const [procedure, setProcedure] = useState<Procedure | null>(null);

  useEffect(() => {
    setProcedure(procedureInfo?.procedure || null);
  }, [procedureInfo]);

  function handleValidation(e?: MouseEvent<HTMLButtonElement>) {
    e?.preventDefault();

    if (!procedure || !procedureInfo?.procedure_id) {
      toast.error('Selecione um procedimento');
      return;
    }

    if (procedureInfo.sale_price_min <= 0) {
      toast.error('Informe o preço de venda mínimo');
      return;
    }

    if (procedureInfo.sale_price_max <= 0) {
      toast.error('Informe o preço de venda máximo');
      return;
    }

    if (procedureInfo.sale_price_max < procedureInfo.sale_price_min) {
      toast.error('O preço de venda máximo não pode ser menor que o preço de venda mínimo');
      return;
    }

    handleAddProcedure({
      ...procedureInfo,
      procedure: procedure,
      procedure_id: procedure.id,
    });
  }
  return (
    <Dialog
      onExited={onExited}
      disableEscapeKeyDown
      disableBackdropClick
      maxWidth="sm"
      title={type === 'edit' ? 'Editar procedimento' : 'Adicionar procedimento'}
    >
      <Container>
        <ProceduresAutoComplete
          label="Procedimento"
          onSelect={procedure => {
            handleChangeProcedureInfo('procedure_id', procedure?.id || '');
            handleChangeProcedureInfo('procedure', procedure);
            setProcedure(procedure);
          }}
          placeholder="Selecione um procedimento"
          selected={procedure}
        />

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Preço de venda mínimo"
            placeholder="Digite o preço de venda mínimo"
            value={procedureInfo?.sale_price_min || 0}
            onChange={e => handleChangeProcedureInfo('sale_price_min', e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputComponent: asInputComponent(MoneyInput),
              },
            }}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <TextField
            label="Preço de venda máximo"
            placeholder="Digite o preço de venda máximo"
            value={procedureInfo?.sale_price_max || 0}
            onChange={e => handleChangeProcedureInfo('sale_price_max', e.target.value)}
            fullWidth
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                inputComponent: asInputComponent(MoneyInput),
              },
            }}
          />
        </Grid>

        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onExited}>
            Cancelar
          </Button>
          <Button variant="contained" color="primary" onClick={handleValidation}>
            {type === 'new' ? 'Adicionar' : 'Salvar'}
          </Button>
        </DialogActions>
      </Container>
    </Dialog>
  );
};

export default HospitalProcedureForm;
